/* "How hydrogen works": scroll-scrubbed film (canvas + JPEG frames), beat overlays and a scale HUD.
   Frames decode off-thread into an ImageBitmap window around the playhead, so every draw is a blit.
   Dev contract: ?jump=<scrollY> lands pre-scrolled and settled; window.__ready flips when drawn. */
(() => {
  const film = document.querySelector("[data-hf]");
  if (!film) return;
  const cv = film.querySelector("canvas"), ctx = cv.getContext("2d", { alpha: false });
  // portrait phones get frames pre-cropped around the subject (640×900): ~half the bytes of the 1600×900 set
  const PORTRAIT = innerWidth / innerHeight < 0.8 && !!film.dataset.baseM;
  const N = +film.dataset.frames, BASE = PORTRAIT ? film.dataset.baseM : film.dataset.base;
  const reduced = matchMedia("(prefers-reduced-motion: reduce)").matches;
  const beats = [...film.querySelectorAll("[data-in]")].map((el) => ({ el, in: +el.dataset.in, peak: +el.dataset.peak, hold: +(el.dataset.hold || el.dataset.peak), out: +el.dataset.out }));
  const scaleEl = film.querySelector("[data-scale]"), bar = film.querySelector("[data-bar]");
  const chapters = [...film.querySelectorAll("[data-ch]")], fade = film.querySelector("[data-fade]");
  const loader = film.querySelector("[data-loader]");
  // "Scroll to begin" on the intro: glide to the start of the film
  document.querySelector("[data-go]")?.addEventListener("click", (e) => {
    e.preventDefault();
    scrollTo({ top: scrollY + film.getBoundingClientRect().top, behavior: reduced ? "auto" : "smooth" });
  });
  const JUMP = new URLSearchParams(location.search).get("jump");
  if (JUMP !== null) history.scrollRestoration = "manual";

  // scale readout, interpolated on a log scale between story waypoints (metres)
  const SCALE = [[0, 1.8], [0.17, 2e-4], [0.36, 5e-5], [0.47, 1e-6], [0.65, 1e-6], [0.83, 1e-5], [1, 1e-4]];
  const nf1 = (v) => v.toLocaleString(document.documentElement.lang || "en", { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  const fmtScale = (m) => m >= 1 ? nf1(m) + " m" : m >= 1e-3 ? nf1(m * 1e3) + " mm" : m >= 1e-6 ? Math.round(m * 1e6) + " µm" : Math.round(m * 1e9) + " nm";
  const scaleAt = (p) => {
    for (let i = 1; i < SCALE.length; i++) if (p <= SCALE[i][0]) {
      const [p0, a] = SCALE[i - 1], [p1, b] = SCALE[i], k = (p - p0) / (p1 - p0 || 1);
      return Math.exp(Math.log(a) + (Math.log(b) - Math.log(a)) * k);
    }
    return SCALE[SCALE.length - 1][1];
  };

  // horizontal focal point of the footage (0..1) along the film: the figure sits right of centre at the start
  const FOCUS = [[0, 0.64], [0.1, 0.56], [0.2, 0.5], [1, 0.5]];
  const focusAt = (p) => { if (PORTRAIT) return 0.5; for (let i = 1; i < FOCUS.length; i++) if (p <= FOCUS[i][0]) { const [p0, a] = FOCUS[i - 1], [p1, b] = FOCUS[i]; return a + (b - a) * ((p - p0) / (p1 - p0)); } return 0.5; };

  // frames
  const images = new Array(N), bitmaps = new Map(), decoding = new Set();
  let loaded = 0, displayed = -1, bmpCenter = -999;
  const SUFFIX = film.dataset.suffix || ""; // cache-buster from the theme asset URL (?v=...)
  const src = (i) => `${BASE}${String(i + 1).padStart(3, "0")}.jpg${SUFFIX}`;
  const nearest = (i) => { for (let d = 0; d < N; d++) { if (images[i - d]?.complete && images[i - d].naturalWidth) return i - d; if (images[i + d]?.complete && images[i + d].naturalWidth) return i + d; } return -1; };
  const ensureBitmaps = (c) => {
    if (Math.abs(c - bmpCenter) < 3) return;
    bmpCenter = c;
    for (let i = Math.max(0, c - 18); i <= Math.min(N - 1, c + 18); i++) {
      if (bitmaps.has(i) || decoding.has(i) || !images[i]?.naturalWidth) continue;
      decoding.add(i);
      createImageBitmap(images[i]).then((b) => {
        decoding.delete(i);
        if (Math.abs(i - bmpCenter) > 28) { b.close(); return; }
        bitmaps.set(i, b); if (i === displayed) draw(i, true);
      }).catch(() => decoding.delete(i));
    }
    for (const k of [...bitmaps.keys()]) if (Math.abs(k - c) > 28) { bitmaps.get(k).close(); bitmaps.delete(k); }
  };
  let W = 0, H = 0;
  const size = () => {
    const d = Math.min(1.5, devicePixelRatio || 1);
    W = cv.clientWidth; H = cv.clientHeight; cv.width = Math.round(W * d); cv.height = Math.round(H * d);
    displayed = -1;
  };
  const draw = (i, force) => {
    if (i === displayed && !force) return;
    const img = bitmaps.get(i) || images[nearest(i)];
    if (!img || !(img.width || img.naturalWidth)) return;
    const iw = img.width || img.naturalWidth, ih = img.height || img.naturalHeight;
    const s = Math.max(cv.width / iw, cv.height / ih), w = iw * s, h = ih * s;
    // on narrow screens the 16:9 film is cropped hard: keep the subject in view (focal x per frame)
    const fx = focusAt(i / (N - 1)), x = Math.min(0, Math.max(cv.width - w, cv.width / 2 - w * fx));
    ctx.drawImage(img, x, (cv.height - h) / 2, w, h);
    displayed = i;
  };
  const pump = () => new Promise((done) => {
    let next = 0, active = 0;
    const order = [...Array.from({ length: Math.ceil(N / 2) }, (_, i) => i * 2), ...Array.from({ length: Math.floor(N / 2) }, (_, i) => i * 2 + 1)];
    const go = () => {
      while (active < 10 && next < order.length) {
        const i = order[next++], im = new Image(); active++;
        im.decoding = "async"; im.src = src(i); images[i] = im;
        const fin = () => { active--; loaded++; if (loader) loader.style.setProperty("--p", loaded / N); if (i === 0) draw(0, true); if (loaded === N) done(); else go(); };
        im.onload = fin; im.onerror = fin;
      }
    };
    go();
  });

  // progress + overlays
  const scrollP = () => { const r = film.getBoundingClientRect(); return Math.max(0, Math.min(1, -r.top / (r.height - innerHeight))); };
  // time warp: scroll progress -> film progress. The cell's response (the nudge, mitohormesis, the light
  // spreading; film 0.48-0.83) gets ~1.5x the scroll distance so the visitor can linger in it.
  const WARP = [[0, 0], [0.26, 0.477], [0.72, 0.826], [1, 1]];
  const warp = (p) => { for (let i = 1; i < WARP.length; i++) if (p <= WARP[i][0]) { const [a0, b0] = WARP[i - 1], [a1, b1] = WARP[i]; return b0 + (b1 - b0) * ((p - a0) / (a1 - a0)); } return 1; };
  const progress = () => warp(scrollP());
  const alpha = (b, p) => {
    if (p < b.in || p > b.out) return 0;
    if (p < b.peak) return (p - b.in) / Math.max(1e-4, b.peak - b.in);
    if (b.out > 1.5 || p <= b.hold) return 1;
    return 1 - (p - b.hold) / Math.max(1e-4, b.out - b.hold);
  };
  let cur = 0, lastScale = "";
  const overlays = (p) => {
    for (const b of beats) {
      const a = Math.max(0, Math.min(1, alpha(b, p)));
      b.el.style.opacity = a.toFixed(3);
      b.el.style.transform = `translate3d(0, ${((1 - a) * (p < b.peak ? 24 : -24)).toFixed(1)}px, 0)`;
      b.el.style.visibility = a > 0.01 ? "visible" : "hidden";
    }
    const s = fmtScale(scaleAt(p)); if (s !== lastScale && scaleEl) { scaleEl.textContent = s; lastScale = s; }
    if (bar) bar.style.setProperty("--p", p.toFixed(4));
    let ch = 0; chapters.forEach((c, i) => { if (p >= +(c.dataset.at || 0)) ch = i; });
    chapters.forEach((c, i) => c.toggleAttribute("data-on", i === ch));
    if (fade) fade.style.opacity = Math.max(0, (p - 0.93) / 0.07).toFixed(3);
  };
  let onScreen = true, lastP = -1;
  if ("IntersectionObserver" in window) new IntersectionObserver(([e]) => { onScreen = e.isIntersecting; }, { rootMargin: "200px 0px" }).observe(film);
  const tick = () => {
    if (!onScreen) { requestAnimationFrame(tick); return; }
    const target = progress() * (N - 1);
    cur += (target - cur) * 0.14;
    if (Math.abs(target - cur) < 0.01) cur = target;
    const i = Math.round(cur);
    ensureBitmaps(i); draw(i);
    const p = cur / (N - 1); if (Math.abs(p - lastP) > 1e-4) { overlays(p); lastP = p; }
    requestAnimationFrame(tick);
  };

  size(); addEventListener("resize", () => { size(); draw(Math.round(cur), true); });
  if (reduced) film.classList.add("hf-reduced");
  pump().then(() => {
    film.classList.add("hf-loaded");
    if (JUMP !== null) { scrollTo(0, +JUMP || 0); cur = progress() * (N - 1); }
    ensureBitmaps(Math.round(cur)); draw(Math.round(cur), true); overlays(cur / (N - 1));
    requestAnimationFrame(() => requestAnimationFrame(() => { window.__ready = true; }));
  });
  draw(0, true); overlays(0);
  requestAnimationFrame(tick);
})();
